import { z } from 'zod';

type StringEnum = Readonly<Record<string, string>>;

interface TextValidationOptions {
    trim?: boolean;
    typeMessage?: string;
}

interface RequiredTextValidationOptions extends TextValidationOptions {
    rejectWhitespace?: boolean;
}

/** Подготавливает проверку строки с выбранной обработкой пробелов */
const textSchema = ({ trim = false, typeMessage }: TextValidationOptions = {}) => {
    const schema = z.string(typeMessage
        ? {
            error: typeMessage,
        }
        : undefined);

    return trim
        ? schema.trim()
        : schema;
};

/** Проверяет заполнение текстового значения */
export const requiredString = (
    message?: string,
    options: RequiredTextValidationOptions = {},
) => {
    const schema = textSchema({
        ...options,
        trim: options.trim ?? true,
    })
        .min(1, message);

    return options.rejectWhitespace
        ? schema.refine((value) => value.trim().length > 0, message)
        : schema;
};

/** Проверяет обязательный текст */
export const requiredText = (
    fieldName: string,
    message = `${fieldName} обязательно`,
) => requiredString(message);

/** Ограничивает длину текста */
export const textWithinLimit = (
    maximum: number,
    message?: string,
    options?: TextValidationOptions,
) => textSchema(options)
    .max(maximum, message);

/** Проверяет заполнение и длину обязательного текста */
export const requiredTextWithinLimit = (fieldName: string, maximum: number) => textWithinLimit(maximum, `${fieldName}: не более ${maximum} символов`)
    .pipe(requiredText(fieldName, `Заполните поле «${fieldName}»`));

/** Создаёт правило для обязательного содержимого HTML-редактора */
export const requiredRichText = (message: string) => z.string()
    .refine((value) => {
        const text = value
            .replace(/<(script|style)\b[^>]*>[\s\S]*?<\/\1>/gi, '')
            .replace(/<[^>]*>/g, '')
            .replace(/&(?:nbsp|#160|#x[aA]0);/g, ' ')
            .replace(/[\s\u200B-\u200D\uFEFF]/g, '');

        return text.length > 0 || /<(?:img|video)\b[^>]*\bsrc\s*=\s*["'][^"']+["']/i.test(value);
    }, {
        message,
    });

/** Создаёт правило для числового значения */
export const requiredNumber = (message: string) => z.number({
    message,
})
    .nullable()
    .superRefine((value, context) => {
        if (value === null) context.addIssue({
            code: 'custom',
            message,
        });
    });

/** Создаёт правило для обязательного значения перечисления */
export const requiredEnum = <const TEnum extends StringEnum>(
    values: TEnum,
    message: string,
) => z.enum(values)
    .nullable()
    .superRefine((value, context) => {
        if (value === null) context.addIssue({
            code: 'custom',
            message,
        });
    });
